import React, { useState } from 'react';
import { I } from '../icons-ds';
import { SidebarItem } from './SidebarItem';
import type { Folder } from '../lib/types';

export type FolderTreeItemProps = {
  folder: Folder;
  active?: string;
  onNavigate: (target: string) => void;
  defaultExpanded?: boolean;
};

export function FolderTreeItem({
  folder,
  active,
  onNavigate,
  defaultExpanded,
}: FolderTreeItemProps) {
  const [expanded, setExpanded] = useState(defaultExpanded ?? folder.id === 'inpatient');
  const [hover, setHover] = useState(false);
  const isActive = active === 'folder:' + folder.id;
  const totalCount =
    folder.count ??
    (folder.children ? folder.children.reduce((s, c) => s + (c.count || 0), 0) : 0);

  return (
    <div>
      <div
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        style={{
          display: 'flex', alignItems: 'center', gap: 4,
          padding: '8px 6px 8px 4px',
          borderRadius: 8,
          background: isActive ? 'var(--accent-fill)' : hover ? 'var(--bg-fill-1)' : 'transparent',
          transition: 'background var(--dur-fast) var(--ease-out)',
        }}
      >
        <button
          onClick={(e) => {
            e.stopPropagation();
            setExpanded((v) => !v);
          }}
          aria-label={expanded ? 'Collapse' : 'Expand'}
          style={{
            width: 18, height: 18,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            color: 'var(--fg-tertiary)',
            transition: 'transform var(--dur-fast) var(--ease-out)',
            transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)',
            background: 'transparent', border: 0, cursor: 'pointer', padding: 0,
          }}
        >
          <I.chevDown size={12} stroke={2} />
        </button>
        <button
          onClick={() => onNavigate('folder:' + folder.id)}
          style={{
            display: 'flex', alignItems: 'center', gap: 10,
            flex: 1, minWidth: 0,
            color: isActive ? 'var(--accent)' : 'var(--fg-primary)',
            fontSize: 14, fontWeight: isActive ? 600 : 500, letterSpacing: '-0.16px',
            textAlign: 'left',
            background: 'transparent', border: 0, cursor: 'pointer', padding: 0,
          }}
        >
          <span
            style={{
              width: 10, height: 10, borderRadius: 3,
              background: folder.color,
              display: 'inline-block', flex: '0 0 auto',
            }}
          />
          <span
            style={{
              flex: 1, minWidth: 0,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}
          >
            {folder.name}
          </span>
          <span
            style={{
              fontSize: 11, fontWeight: 600,
              color: 'var(--fg-tertiary)', padding: '1px 6px',
            }}
          >
            {totalCount}
          </span>
        </button>
      </div>
      {expanded && folder.children && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 1, padding: '2px 0 4px' }}>
          {folder.children.map((child) => (
            <SidebarItem
              key={child.id}
              indent={22}
              onClick={() => onNavigate('folder:' + folder.id + '/' + child.id)}
              active={active === 'folder:' + folder.id + '/' + child.id}
              icon={<I.folder size={14} />}
              count={child.count}
            >
              {child.name}
            </SidebarItem>
          ))}
        </div>
      )}
    </div>
  );
}

export default FolderTreeItem;
